import { useCallback, useSyncExternalStore } from 'react';

/* Локальное хранилище клиента. В Capacitor WebView и в приватном режиме
   браузера localStorage может быть недоступен — всё через try/catch, при
   отказе значения живут в памяти до перезагрузки. */

const P = 'tg.client.';
const mem = new Map<string, unknown>();
const subs = new Map<string, Set<() => void>>();

export function lsGet<T>(key: string, def: T): T {
  if (mem.has(key)) return mem.get(key) as T;
  let v: T = def;
  try {
    const raw = localStorage.getItem(P + key);
    if (raw != null) v = JSON.parse(raw) as T;
  } catch {
    /* хранилище недоступно или испорчено — значение по умолчанию */
  }
  mem.set(key, v);
  return v;
}

export function lsSet<T>(key: string, v: T) {
  mem.set(key, v);
  try {
    if (v === undefined) localStorage.removeItem(P + key);
    else localStorage.setItem(P + key, JSON.stringify(v));
  } catch {
    /* хранилище недоступно — живёт в памяти */
  }
  subs.get(key)?.forEach((f) => f());
}

/** Удалить всё своё: выход и удаление аккаунта */
export function lsClear(keep: string[] = []) {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(P) && !keep.includes(k.slice(P.length))) localStorage.removeItem(k);
    }
  } catch {
    /* нечего чистить */
  }
  for (const k of [...mem.keys()]) if (!keep.includes(k)) mem.delete(k);
  subs.forEach((s) => s.forEach((f) => f()));
}

/** Реактивное значение из хранилища: черновики, открытые секции, выбранные табы */
export function useLocal<T>(key: string, def: T): [T, (v: T | ((p: T) => T)) => void] {
  const sub = useCallback(
    (f: () => void) => {
      let s = subs.get(key);
      if (!s) subs.set(key, (s = new Set()));
      s.add(f);
      return () => s!.delete(f);
    },
    [key],
  );
  const v = useSyncExternalStore(sub, () => lsGet(key, def));
  const set = useCallback(
    (nv: T | ((p: T) => T)) => lsSet(key, typeof nv === 'function' ? (nv as (p: T) => T)(lsGet(key, def)) : nv),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
  return [v, set];
}
